import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@postdaily/ui/components/alert-dialog";
import { Button } from "@postdaily/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@postdaily/ui/components/card";
import { Input } from "@postdaily/ui/components/input";
import { Label } from "@postdaily/ui/components/label";
import { Spinner } from "@postdaily/ui/components/spinner";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Download, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { PageHeader } from "@/components/page-header";
import { authClient } from "@/lib/auth-client";
import { resetAnalyticsIdentity } from "@/lib/posthog";
import { useTRPC } from "@/utils/trpc";

export const Route = createFileRoute("/(app)/settings/account")({
  component: AccountSettingsPage,
});

function AccountSettingsPage() {
  const trpc = useTRPC();
  const viewer = useQuery(trpc.viewer.me.queryOptions());
  const email = viewer.data?.user.email ?? "";

  const exportQuery = useQuery({
    ...trpc.viewer.exportData.queryOptions(),
    enabled: false,
  });

  const [confirmEmail, setConfirmEmail] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);

  const workspacesQuery = useQuery({
    ...trpc.workspaces.list.queryOptions(),
    enabled: deleteOpen,
  });

  const ownedWorkspaces =
    workspacesQuery.data?.filter((workspace) => workspace.role === "owner") ??
    [];
  const memberWorkspaces =
    workspacesQuery.data?.filter((workspace) => workspace.role !== "owner") ??
    [];

  const deleteAccount = useMutation(
    trpc.viewer.deleteAccount.mutationOptions({
      onError: (error) => {
        toast.error(error.message);
      },
      onSuccess: async () => {
        await authClient.signOut();
        // The server has already asked PostHog to erase this person; this
        // drops their id from the browser so nothing is sent under it again.
        resetAnalyticsIdentity();
        toast.success("Your account has been deleted.");
        // Full page load — see the note in components/nav-user.tsx: a
        // client-side navigation can reach /login while the auth store still
        // holds the deleted session, which bounces straight back into the app.
        window.location.href = "/login";
      },
    }),
  );

  async function handleExport() {
    const { data } = await exportQuery.refetch();
    if (!data) {
      toast.error("Could not build your export. Try again.");
      return;
    }
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "postdaily-data-export.json";
    anchor.click();
    URL.revokeObjectURL(url);
  }

  const canDelete =
    Boolean(email) && confirmEmail.trim().toLowerCase() === email.toLowerCase();

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        size="lg"
        title="Account & data"
        description="Export or permanently delete your personal data."
      />

      <Card>
        <CardHeader>
          <CardTitle>Export your data</CardTitle>
          <CardDescription>
            Download a JSON file with your profile, workspace memberships, the
            posts you've authored, the channels you've connected, and your AI
            assistant chats and brand-voice settings. Chat transcripts aren't in
            the file — read those in the composer's Assistant panel.
          </CardDescription>
        </CardHeader>
        <CardFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleExport}
            disabled={exportQuery.isFetching}
          >
            {exportQuery.isFetching ? (
              <Spinner data-icon="inline-start" />
            ) : (
              <Download data-icon="inline-start" />
            )}
            Download my data
          </Button>
        </CardFooter>
      </Card>

      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle className="text-destructive">Delete account</CardTitle>
          <CardDescription>
            This permanently deletes the workspaces you own (and all their
            posts, channels and media), removes you from other workspaces, and
            erases your personal data — including your AI assistant chats and
            their transcripts, everywhere you had them. This cannot be undone.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex max-w-sm flex-col gap-2">
            <div className="flex flex-col gap-1">
              <Label htmlFor="confirm-email">Type your email to confirm</Label>
              <p className="break-all font-mono text-muted-foreground text-sm">
                {email}
              </p>
            </div>
            <Input
              id="confirm-email"
              value={confirmEmail}
              onChange={(event) => setConfirmEmail(event.currentTarget.value)}
              placeholder={email}
              autoComplete="off"
            />
          </div>
        </CardContent>
        <CardFooter>
          <Button
            type="button"
            variant="destructive"
            disabled={deleteAccount.isPending || !canDelete}
            onClick={() => setDeleteOpen(true)}
          >
            {deleteAccount.isPending ? (
              <Spinner data-icon="inline-start" />
            ) : (
              <Trash2 data-icon="inline-start" />
            )}
            Delete my account
          </Button>
        </CardFooter>
      </Card>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="flex max-h-[85vh] flex-col gap-0 overflow-hidden sm:max-w-md">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pb-4">
            <AlertDialogHeader>
              <AlertDialogTitle>
                Delete your account permanently?
              </AlertDialogTitle>
              <AlertDialogDescription>
                Review what will be removed. You will be signed out immediately.
                This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>

            {workspacesQuery.isLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Spinner className="size-4" />
                Loading what will be deleted…
              </div>
            ) : (
              <div className="space-y-4 text-sm">
                <div className="space-y-2">
                  <p className="font-medium">Will be permanently deleted</p>
                  <ul className="list-disc space-y-1.5 pl-5 text-muted-foreground">
                    {ownedWorkspaces.map((workspace) => (
                      <li key={workspace.id}>
                        <span className="font-medium text-foreground">
                          {workspace.name}
                        </span>
                        {" — "}
                        workspace and all of its posts, channels, and media
                      </li>
                    ))}
                    {ownedWorkspaces.length === 0 ? (
                      <li>No owned workspaces</li>
                    ) : null}
                    <li>Your profile, email, and personal data</li>
                    <li>Login credentials, active sessions, and 2FA</li>
                  </ul>
                </div>

                {memberWorkspaces.length > 0 ? (
                  <div className="space-y-2">
                    <p className="font-medium">You will leave</p>
                    <ul className="list-disc space-y-1.5 pl-5 text-muted-foreground">
                      {memberWorkspaces.map((workspace) => (
                        <li key={workspace.id}>
                          <span className="font-medium text-foreground">
                            {workspace.name}
                          </span>
                          {" — "}
                          membership removed (workspace stays)
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            )}
          </div>

          <AlertDialogFooter className="shrink-0">
            <AlertDialogCancel disabled={deleteAccount.isPending}>
              Keep account
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={deleteAccount.isPending || workspacesQuery.isLoading}
              onClick={() => {
                deleteAccount.mutate();
                setDeleteOpen(false);
              }}
            >
              {deleteAccount.isPending ? (
                <Spinner data-icon="inline-start" />
              ) : null}
              Delete forever
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
